import { createAsyncThunk, createSlice, current } from '@reduxjs/toolkit';
import { getDeletedReactions, getUpdatedReactions } from './helpers';
import { FILE_COMMENTS, FILE_URL } from '../../utils/apiEndpoints';
import { genWorkspaceHeader } from '../../utils/constants';
import clientService from '../../apiClient';

const addHasReplies = (data, parentCommentId) => {
  const updatedComments = Object.entries(data).reduce((acc, [commentId, replies]) => {
    const updatedReplies = replies.map((reply) =>
      reply?.id === parentCommentId ? { ...reply, has_replies: true } : reply
    );
    acc[commentId] = updatedReplies;
    return acc;
  }, {});
  return updatedComments;
};

const addCommentReplyEditorUseCase = ({ data, commentId, comments }) => {
  const output = comments?.map((comment) => {
    if (comment.id === commentId)
      return {
        ...comment,
        has_replies: true,
        replies: [...(comment?.replies || []), { ...data }],
      };
    return comment;
  });
  return output;
};

// use-case
const addCommentReactionEditorUseCase = ({
  isReply,
  parentCommentId,
  reaction,
  data,
  username,
  commentId,
  comments,
}) => {
  let output = comments.map((c) => {
    if (isReply && c.id === parentCommentId) {
      return {
        ...c,
        replies: c?.replies?.map((r) =>
          r.id === commentId ? { ...r, reactions: getUpdatedReactions(r, reaction, username, data) } : r
        ),
      };
    }
    if (c.id === commentId) return { ...c, reactions: getUpdatedReactions(c, reaction, username, data) };
    return c;
  });
  return output;
};

const editCommentEditorUseCase = ({ comments, parentCommentId, isReply, commentId, data }) => {
  const output = comments?.map((c) => {
    if (isReply && c.id === parentCommentId)
      return {
        ...c,
        replies: c?.replies?.map((r) => (r?.id === commentId ? { ...r, comment: data?.comment } : r)),
      };
    if (c.id === commentId) return { ...c, comment: data?.comment, status: data?.status };
    return c;
  });
  return output;
};

const deleteCommentReactionEditorUseCase = ({ comments, isReply, parentCommentId, reaction, username, commentId }) => {
  const output = comments.map((c) => {
    if (isReply && c.id === parentCommentId) {
      return {
        ...c,
        replies: c?.replies?.map((r) =>
          r.id === commentId ? { ...r, reactions: getDeletedReactions(r.reactions, reaction, username) } : r
        ),
      };
    }
    if (c.id === commentId) return { ...c, reactions: getDeletedReactions(c.reactions, reaction, username) };
    return c;
  });
  return output;
};

const deleteCommentEditorUseCase = ({ comments, commentId }) => {
  let output = comments?.filter((comment) => comment.id !== commentId);
  if (output.length === comments?.length) {
    output = comments?.map((c) => ({
      ...c,
      replies: c.replies.filter((r) => r.id !== commentId),
    }));
  }
  return output;
};

// thunks
export const resolveComment = createAsyncThunk('editor/resolveComment', async ({ commentId, fileId, workspaceId }) => {
  const response = await clientService.put(
    `${FILE_URL}s/${fileId}/threads/${commentId}`,
    { status: 'CLOSED' },
    { headers: genWorkspaceHeader(workspaceId) }
  );
  return { data: response?.data, commentId };
});

export const fetchDocumentReactions = createAsyncThunk(
  'editor/fetchdocumentreactions',
  async ({ file_id, workspace_id }) => {
    try {
      const response = await clientService.get(`${FILE_URL}/${file_id}/reaction`, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (error) {
      return error;
    }
  }
);

export const addDocumentReaction = createAsyncThunk(
  'editor/adddocumentreactions',
  async ({ file_id, body, workspace_id }) => {
    try {
      const response = await clientService.put(`${FILE_URL}/${file_id}/reaction`, body, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (error) {
      return error;
    }
  }
);

export const removeDocumentReaction = createAsyncThunk(
  'editor/removedocumentreactions',
  async ({ file_id, body, reaction, workspace_id }) => {
    try {
      const response = await clientService.put(`${FILE_URL}/${file_id}/reaction`, body, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return { response, reaction };
    } catch (error) {
      return error;
    }
  }
);
export const fetchComments = createAsyncThunk(
  'editor/fetchComments',
  async ({ file_id, skip = 0, limit = 30, workspace_id }) => {
    try {
      const response = await clientService.get(`${FILE_COMMENTS}/file/${file_id}/comment?skip=${skip}&limit=${limit}`, {
        headers: genWorkspaceHeader(workspace_id),
      });

      return response;
    } catch (error) {
      throw error; // Re-throw the error if needed for further handling
    }
  }
);

export const addComment = createAsyncThunk('editor/addComment', async ({ file_id, content, workspace_id, name }) => {
  try {
    const response = await clientService.post(
      `${FILE_COMMENTS}/file/${file_id}/comment`,
      { content },
      {
        headers: genWorkspaceHeader(workspace_id),
      }
    );
    return { name, comment: response?.data };
  } catch (error) {
    throw error; // Re-throw the error if needed for further handling
  }
});

export const fetchReplies = createAsyncThunk(
  'editor/fetchReplies',
  async ({ commentId, skip = 0, limit = 30, workspace_id }) => {
    try {
      const response = await clientService.get(`${FILE_COMMENTS}/comment/${commentId}`, {
        headers: genWorkspaceHeader(workspace_id),
      });
      return { commentId, ...response };
    } catch (error) {
      throw error; // Re-throw the error if needed for further handling
    }
  }
);

export const addReply = createAsyncThunk('editor/addReply', async ({ name, commentId, content, workspace_id }) => {
  try {
    const response = await clientService.post(
      `${FILE_COMMENTS}/comment/${commentId}/reply`,
      { content },
      { headers: genWorkspaceHeader(workspace_id) }
    );
    return { commentId, ...response, name };
  } catch (error) {
    throw error; // Re-throw the error if needed for further handling
  }
});

export const deleteComment = createAsyncThunk('editor/deleteComment', async ({ commentId, workspace_id, username }) => {
  try {
    const response = await clientService.delete(`${FILE_COMMENTS}/comment/${commentId}`, {
      headers: genWorkspaceHeader(workspace_id),
    });
    return { commentId, response, username };
  } catch (error) {
    throw error;
  }
});

export const editComment = createAsyncThunk(
  'editor/editComment',
  async ({ commentId, content, workspace_id, isReply, parentCommentId }) => {
    try {
      const response = await clientService.put(
        `${FILE_COMMENTS}/comment/${commentId}`,
        { content },
        { headers: genWorkspaceHeader(workspace_id) }
      );
      return { data: response?.data, commentId, isReply, parentCommentId };
    } catch (error) {
      throw error;
    }
  }
);
export const addCommentReaction = createAsyncThunk(
  'editor/addCommentReaction',
  async ({ commentId, reaction, workspace_id, isReply, parentCommentId, username }) => {
    const response = await clientService.put(
      `${FILE_COMMENTS}/comment/${commentId}/reaction`,
      { reaction },
      { headers: genWorkspaceHeader(workspace_id) }
    );
    return { commentId, data: response, isReply, parentCommentId, username };
  }
);

export const deleteCommentReaction = createAsyncThunk(
  'editor/deleteCommentReaction',
  async ({ commentId, reaction, username, workspace_id, isReply, parentCommentId }) => {
    const response = await clientService.put(
      `${FILE_COMMENTS}/comment/${commentId}/reaction`,
      { action: 'DELETE' },
      { headers: genWorkspaceHeader(workspace_id) }
    );
    return { commentId, reaction, data: response, username, isReply, parentCommentId };
  }
);

// all editor comment functionalities
export const fetchEditorComments = createAsyncThunk('editor/fetchEditorComments', async ({ workspaceId, fileId }) => {
  const response = await clientService.get(`${FILE_URL}s/${fileId}/threads?thread_status=OPEN`, {
    headers: genWorkspaceHeader(workspaceId),
  });
  return response?.data?.threads || [];
});

export const addCommentReactionEditor = createAsyncThunk(
  'editor/addCommentReactionEditor',
  async ({ commentId, reaction, isReply, parentCommentId, username, workspaceId }) => {
    const response = await clientService.put(
      `/onedoc_backend/v1/threads/${commentId}/reaction`,
      { reaction },
      { headers: genWorkspaceHeader(workspaceId) }
    );
    return { isReply, parentCommentId, username, data: response?.data, reaction, commentId, workspaceId };
  }
);

export const deleteCommentReactionEditor = createAsyncThunk(
  'editor/deleteCommentReactionEditor',
  async ({ commentId, reaction, isReply, parentCommentId, workspaceId, username }) => {
    const response = await clientService.put(
      `/onedoc_backend/v1/threads/${commentId}/reaction`,
      { action: 'DELETE' },
      { headers: genWorkspaceHeader(workspaceId) }
    );
    return { data: response?.data, commentId, reaction, isReply, parentCommentId, workspaceId, username };
  }
);

export const addNewCommentEditor = createAsyncThunk(
  'editor/addNewCommentEditor',
  async ({ body, workspaceId, fileId }) => {
    const response = await clientService.post(`${FILE_URL}s/${fileId}/threads`, body, {
      headers: genWorkspaceHeader(workspaceId),
    });
    return { data: response?.data, workspaceId, fileId };
  }
);

export const addCommentReplyEditor = createAsyncThunk(
  'editor/addCommentReplyEditor',
  async ({ commentId, comment, workspaceId, fileId }) => {
    const response = await clientService.post(
      `${FILE_URL}s/${fileId}/threads/${commentId}/comments`,
      { comment },
      { headers: genWorkspaceHeader(workspaceId) }
    );
    return { commentId, data: response?.data };
  }
);

export const deleteCommentEditor = createAsyncThunk(
  'editor/deleteCommentEditor',
  async ({ commentId, workspaceId }) => {
    const response = await clientService.delete(`${FILE_COMMENTS}/comment/${commentId}`, {
      headers: genWorkspaceHeader(workspaceId),
    });
    return { data: response?.data, commentId, workspaceId };
  }
);

export const editCommentEditor = createAsyncThunk(
  'editor/editCommentEditor',
  async ({ commentId, fileId, content, workspaceId, isReply, parentCommentId }) => {
    const body = { comment: content };
    let response = null;
    if (!isReply)
      response = await clientService.put(`${FILE_URL}s/${fileId}/threads/${commentId}`, body, {
        headers: genWorkspaceHeader(workspaceId),
      });
    else
      response = await clientService.put(`/onedoc_backend/v1/threads/${parentCommentId}/comments/${commentId}`, body, {
        headers: genWorkspaceHeader(workspaceId),
      });
    return { data: response?.data, commentId, isReply, parentCommentId };
  }
);

const initialState = {
  reactions: {},
  loading: false,
  error: null,
  comments: [],
  totalComments: 0,
  loading: false,
  loadingMore: false,
  error: null,
  repliesMap: {}, // Store replies for each comment
  repliesLoadingMap: {}, // Track loading state for each comment's replies
  repliesTotalMap: {}, // Track total replies for each comment
  currentSkip: 0,
  hasMore: true,
  editingCommentId: null,
  commentReactionsLoadingMap: {}, // Track loading state for each comment's reactions
  commentReactionsErrorMap: {},
  editorCommentLoaded: {},
  editorComments: [],
  editorCommentsLoading: false,
  documentReactionError: false,
};

const editorSlice = createSlice({
  name: 'editor',
  initialState,
  reducers: {
    cleanRepliesMap: (state) => {
      state.repliesMap = {};
      state.repliesLoadingMap = {};
      state.repliesTotalMap = {};
    },

    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setEditingCommentId: (state, action) => {
      state.editingCommentId = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch reactions
      .addCase(fetchDocumentReactions.pending, (state) => {
        state.loading = true;
        state.documentReactionError = true;
      })
      .addCase(fetchDocumentReactions.fulfilled, (state, action) => {
        state.loading = false;
        state.reactions = action.payload.data;
        state.documentReactionError = false;
        state.error = null;
      })
      .addCase(fetchDocumentReactions.rejected, (state, action) => {
        state.loading = false;
        state.documentReactionError = true;
        state.error = action.error.message;
      })

      // Add reaction
      .addCase(addDocumentReaction.fulfilled, (state, action) => {
        const { reaction, reaction_name: name, actioner_name, username } = action.payload.data;

        // First, remove the user from their previous reaction if any
        Object.entries(state.reactions).forEach(([key, data]) => {
          if (data.users.some((user) => user.username === username)) {
            const updatedUsers = data.users.filter((user) => user.username !== username);
            if (updatedUsers.length === 0) {
              delete state.reactions[key];
            } else {
              state.reactions[key] = {
                ...data,
                users: updatedUsers,
                count: updatedUsers.length,
              };
            }
          }
        });

        // Then add the new reaction
        if (!state.reactions[reaction]) {
          state.reactions[reaction] = {
            name,
            count: 1,
            users: [
              {
                name: actioner_name,
                username,
                reaction_updated_at: action.payload.data.updated_at,
              },
            ],
          };
        } else {
          state.reactions[reaction].count++;
          state.reactions[reaction].users.push({
            name: actioner_name,
            username,
            reaction_updated_at: action.payload.data.updated_at,
          });
        }
      })

      // Remove reaction
      .addCase(removeDocumentReaction.fulfilled, (state, action) => {
        const { reaction } = action.payload;

        if (state.reactions[reaction]) {
          if (state.reactions[reaction].count <= 1) {
            delete state.reactions[reaction];
          } else {
            state.reactions[reaction].count--;
            // Remove the user from the users array
            // Note: In a real implementation, you'd want to remove the specific user
            state.reactions[reaction].users.pop();
          }
        }
      })
      .addCase(fetchComments.pending, (state, action) => {
        const { skip } = action.meta.arg;
        state.loading = skip === 0;
        state.loadingMore = skip > 0;
      })
      .addCase(fetchComments.fulfilled, (state, action) => {
        const { skip = 0 } = action.meta.arg;
        state.loading = false;
        state.loadingMore = false;

        if (skip === 0) {
          state.comments = action.payload.data?.comments;
        } else {
          state.comments = [...state.comments, ...action.payload.data.comments];
        }

        state.totalComments = action.payload.data?.total;
        state.currentSkip = skip + action?.payload?.data?.comments?.length;
        state.hasMore = state.comments?.length < action.payload.data.total;
      })
      .addCase(fetchComments.rejected, (state, action) => {
        state.loading = false;
        state.loadingMore = false;
        state.error = action.error.message;
      })

      // Add Comment
      .addCase(addComment.fulfilled, (state, action) => {
        const { comment, name } = action.payload;
        state.comments = [{ ...comment, name }, ...(state.comments ?? [])];
        state.totalComments += 1;
      })
      // Fetch Replies
      .addCase(fetchReplies.pending, (state, action) => {
        const { commentId, skip = 0 } = action.meta.arg;
        state.repliesLoadingMap[commentId] = true;
      })
      .addCase(fetchReplies.fulfilled, (state, action) => {
        const { commentId } = action.payload;
        const { skip = 0 } = action.meta.arg;

        state.repliesLoadingMap[commentId] = false;

        if (!state.repliesMap[commentId] || skip === 0) {
          state.repliesMap[commentId] = action.payload.data.comments;
        } else {
          state.repliesMap[commentId] = [...(state.repliesMap[commentId] || []), ...action.payload.data.comments];
        }

        state.repliesTotalMap[commentId] = action.payload.data.total;
      })
      .addCase(fetchReplies.rejected, (state, action) => {
        const { commentId } = action.meta.arg;
        state.repliesLoadingMap[commentId] = false;
      })

      // Add Reply
      .addCase(addReply.fulfilled, (state, action) => {
        const { commentId, data, name } = action.payload;
        if (!state.repliesMap[commentId]) {
          state.repliesMap[commentId] = [];
        }
        state.repliesMap[commentId].push({ ...data, name });
        // Update has_replies flag in parent comment
        const parentComment = state.comments?.find((comment) => comment.id === commentId);
        if (parentComment) {
          parentComment.has_replies = true;
          parentComment.replies = [...(parentComment.replies || []), { ...data, name }];
        } else {
          state.repliesMap = addHasReplies(current(state.repliesMap), commentId);
        }
        // Update total count
        if (state.repliesTotalMap[commentId] !== undefined) {
          state.repliesTotalMap[commentId] += 1;
        } else {
          state.repliesTotalMap[commentId] = 1;
        }
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { commentId } = action.payload;
        // Remove from main comments
        state.comments = state.comments?.filter((comment) => comment.id !== commentId);

        // Remove from replies if it's a reply
        Object.keys(state.repliesMap).forEach((parentId) => {
          state.repliesMap[parentId] = state.repliesMap[parentId].filter((reply) => reply.id !== commentId);

          // Update total count if needed
          if (state.repliesTotalMap[parentId]) {
            state.repliesTotalMap[parentId] -= 1;
          }
        });

        // Decrease total comment count
        state.totalComments -= 1;

        // Clean up replies data if the deleted comment had replies
        if (state.repliesMap[commentId]) {
          delete state.repliesMap[commentId];
          delete state.repliesLoadingMap[commentId];
          delete state.repliesTotalMap[commentId];
        }
      })

      // Edit Comment
      .addCase(editComment.fulfilled, (state, action) => {
        const { data, isReply, parentCommentId, commentId } = action.payload;

        // Update in main comments
        const mainCommentIndex = state.comments?.findIndex((comment) => comment.id === data.id);

        if (mainCommentIndex !== -1) {
          state.comments[mainCommentIndex] = {
            ...state.comments[mainCommentIndex],
            content: data?.content,
          };
        }

        // Update in replies if it's a reply
        Object.keys(state.repliesMap).forEach((parentId) => {
          const replyIndex = state.repliesMap[parentId].findIndex((reply) => reply.id === data?.id);

          if (replyIndex !== -1) {
            state.repliesMap[parentId][replyIndex] = {
              ...state.repliesMap[parentId][replyIndex],
              content: data?.content,
            };
          }
        });

        // Clear editing state
        state.editingCommentId = null;
      })
      .addCase(addCommentReaction.pending, (state, action) => {
        const commentId = action.meta.arg.commentId;
        state.commentReactionsLoadingMap[commentId] = true;
        state.commentReactionsErrorMap[commentId] = null;
      })
      .addCase(addCommentReaction.fulfilled, (state, action) => {
        const { commentId, data, isReply, parentCommentId, username } = action.payload;
        const { reaction, reaction_name: name, actioner_name } = data?.data || {};

        state.commentReactionsLoadingMap[commentId] = false;
        let comment = null;
        if (isReply) comment = state.repliesMap[parentCommentId]?.find((c) => c.id === commentId);
        else comment = state.comments?.find((c) => c.id === commentId);
        if (comment) {
          // Initialize reactions object if it doesn't exist
          if (!comment.reactions) {
            comment.reactions = {};
          }

          // First, remove the user from their previous reaction if any
          Object.entries(comment?.reactions).forEach(([key, reactionData]) => {
            if (reactionData.users.some((user) => user.username === username)) {
              comment.reactions[key].users = reactionData.users.filter((user) => user.username !== username);
              comment.reactions[key].count -= 1;
              if (comment.reactions[key].count === 0) {
                delete comment.reactions[key];
              }
            }
          });

          // Then, add the new reaction
          if (!comment.reactions[reaction]) {
            comment.reactions[reaction] = {
              name,
              count: 1,
              users: [
                {
                  username,
                  name: actioner_name,
                  reaction_updated_at: data.data.updated_at,
                },
              ],
            };
          } else {
            comment.reactions[reaction].count += 1;
            comment.reactions[reaction].users.push({
              username,
              name: actioner_name,
              reaction_updated_at: data.data.updated_at,
            });
          }
        }
      })
      .addCase(addCommentReaction.rejected, (state, action) => {
        const commentId = action.meta.arg.commentId;
        state.commentReactionsLoadingMap[commentId] = false;
        state.commentReactionsErrorMap[commentId] = action.error.message;
      });

    // Delete Comment Reaction
    builder
      .addCase(deleteCommentReaction.pending, (state, action) => {
        const commentId = action.meta.arg.commentId;
        state.commentReactionsLoadingMap[commentId] = true;
        state.commentReactionsErrorMap[commentId] = null;
      })

      .addCase(deleteCommentReaction.fulfilled, (state, action) => {
        const { commentId, reaction, username, isReply, parentCommentId } = action.payload;
        state.commentReactionsLoadingMap[commentId] = false;
        let comment = null;
        if (isReply) comment = state.repliesMap[parentCommentId]?.find((c) => c.id === commentId);
        else comment = state.comments?.find((c) => c.id === commentId);
        if (comment && comment?.reactions && comment?.reactions[reaction]) {
          comment.reactions[reaction].count -= 1;
          comment.reactions[reaction].users = comment.reactions[reaction].users.filter(
            (user) => user.username !== username
          );
          if (comment.reactions[reaction].count === 0) delete comment.reactions[reaction];
          if (Object.keys(comment.reactions).length === 0) delete comment.reactions;
        }
      })
      .addCase(deleteCommentReaction.rejected, (state, action) => {
        const commentId = action.meta.arg.commentId;
        state.commentReactionsLoadingMap[commentId] = false;
        state.commentReactionsErrorMap[commentId] = action.error.message;
      })
      // editor comments usecase
      .addCase(fetchEditorComments.pending, (state) => {
        state.editorCommentsLoading = true;
      })
      .addCase(fetchEditorComments.fulfilled, (state, action) => {
        state.editorCommentsLoading = false;
        const { payload } = action;
        payload?.forEach((c) => {
          state.editorCommentLoaded[c.id] = true;
          if (c?.comments && c?.comments?.length > 0)
            c?.comments?.forEach((r) => (state.editorCommentLoaded[r.id] = true));
        });
        state.editorComments = payload;
      })
      .addCase(fetchEditorComments.rejected, (state, action) => {
        state.editorCommentsLoading = false;
        state.editorComments = [];
        state.editorCommentsLoading = {};
      })
      // editor adding reaction usecase
      .addCase(addCommentReactionEditor.pending, (state, action) => {
        const commentId = action.meta.arg.commentId;
        state.commentReactionsLoadingMap[commentId] = true;
      })
      .addCase(addCommentReactionEditor.fulfilled, (state, action) => {
        const { isReply, parentCommentId, reaction, data, username, commentId } = action.payload;
        const comments = current(state.editorComments);
        state.editorComments = addCommentReactionEditorUseCase({
          isReply,
          parentCommentId,
          reaction,
          data,
          username,
          commentId,
          comments,
        });
        state.commentReactionsLoadingMap[commentId] = false;
      })
      .addCase(addCommentReactionEditor.rejected, (state, action) => {
        const commentId = action.meta.arg.commentId;
        state.commentReactionsLoadingMap[commentId] = false;
      })
      // editor remove reaction usercase
      .addCase(deleteCommentReactionEditor.pending, (state, action) => {
        const commentId = action.meta.arg.commentId;
        state.commentReactionsLoadingMap[commentId] = true;
      })
      .addCase(deleteCommentReactionEditor.fulfilled, (state, action) => {
        const { isReply, parentCommentId, reaction, username, commentId } = action.payload;
        const comments = current(state.editorComments);
        state.editorComments = deleteCommentReactionEditorUseCase({
          comments,
          isReply,
          parentCommentId,
          reaction,
          username,
          commentId,
        });
        state.commentReactionsLoadingMap[commentId] = false;
      })
      .addCase(deleteCommentReactionEditor.rejected, (state, action) => {
        const commentId = action.meta.arg.commentId;
        state.commentReactionsLoadingMap[commentId] = false;
      })
      // delete comment from editor
      .addCase(deleteCommentEditor.fulfilled, (state, action) => {
        const { commentId, workspaceId } = action.payload;
        const comments = current(state.editorComments);
        state.editorComments = deleteCommentEditorUseCase({ comments, commentId, workspaceId });
        state.editorCommentLoaded[commentId] = false;
      })
      .addCase(deleteCommentEditor.rejected, (state, action) => {
        const commentId = action.meta.arg.commentId;
        state.editorCommentLoaded[commentId] = true;
      })
      // reply comment usecase
      .addCase(addCommentReplyEditor.fulfilled, (state, action) => {
        const { commentId, data } = action.payload;
        const comments = current(state.editorComments);
        state.editorComments = addCommentReplyEditorUseCase({ commentId, data, comments });
        state.editorCommentLoaded[data?.id] = true;
      })
      // add new comment usecase
      .addCase(addNewCommentEditor.fulfilled, (state, action) => {
        const { data } = action.payload;
        const commentId = data?.id;
        state.editorComments = [{ ...data }, ...state.editorComments];
        state.editorCommentLoaded[commentId] = true;
      })
      .addCase(editCommentEditor.fulfilled, (state, action) => {
        const { commentId, data, isReply, parentCommentId } = action.payload;
        const comments = current(state.editorComments);
        state.editorComments = editCommentEditorUseCase({ data, commentId, comments, isReply, parentCommentId });
      })
      .addCase(resolveComment.fulfilled, (state, action) => {
        const { commentId } = action.payload;
        if (state.editorCommentLoaded[commentId]) {
          state.editorComments = state.editorComments.filter((c) => c.id !== commentId);
          state.editorCommentLoaded[commentId] = false;
        }
        state.comments = state.comments.filter((c) => c.id !== commentId);
        state.repliesMap[commentId] = undefined;
      });
  },
});
export const { setCurrentPage, setEditingCommentId, cleanRepliesMap } = editorSlice.actions;
export default editorSlice.reducer;
